/**
 * JOTTNAR: Product image viewer
 * Opens a fullscreen Swiper viewer when an image in the main product gallery
 * (mobile slider or desktop grid) is clicked or tapped.
 * - Opens at the image that was tapped, with an "X / Y" counter
 * - Pinch (or double-tap) zooms the image only, capped at 100% of its real size
 * - Uses the Swiper bundle Release already loads globally (layout/theme.liquid)
 */
if (!customElements.get('product-image-viewer')) {
  class ProductImageViewer extends HTMLElement {
    connectedCallback() {
      this.gallery = document.querySelector(this.dataset.gallery);
      this.viewer = this.querySelector('[data-viewer]');
      this.swiperEl = this.querySelector('[data-viewer-swiper]');
      this.closeButton = this.querySelector('[data-viewer-close]');
      this.counter = this.querySelector('[data-viewer-counter]');
      if (!this.gallery || !this.viewer || !this.swiperEl) return;

      this.onGalleryClick = this.onGalleryClick.bind(this);
      this.onGalleryKeydown = this.onGalleryKeydown.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onCloseClick = () => this.viewer.close();
      this.onViewerClick = event => {
        if (event.target === this.viewer) this.viewer.close();
      };

      this.gallery.addEventListener('click', this.onGalleryClick);
      this.gallery.addEventListener('keydown', this.onGalleryKeydown);
      this.closeButton?.addEventListener('click', this.onCloseClick);
      this.viewer.addEventListener('close', this.onClose);
      this.viewer.addEventListener('click', this.onViewerClick);

      this.prepareGallery();
    }

    disconnectedCallback() {
      this.gallery?.removeEventListener('click', this.onGalleryClick);
      this.gallery?.removeEventListener('keydown', this.onGalleryKeydown);
      this.closeButton?.removeEventListener('click', this.onCloseClick);
      this.viewer?.removeEventListener('close', this.onClose);
      this.viewer?.removeEventListener('click', this.onViewerClick);
      if (this.viewer?.open) this.viewer.close();
      document.body.classList.remove('overflow-hidden');
      this.swiper?.destroy(true, true);
      this.swiper = null;
    }

    /** Make gallery images show a zoom cursor and work with the keyboard */
    prepareGallery() {
      this.gallery.classList.add('has-image-viewer');
      this.getGalleryImageCards().forEach(card => {
        if (this.indexFor(card.dataset.mediaId) === null) return;
        // A class rather than role="button" - Swiper's accessibility module sets its own role on slides
        card.classList.add('is-viewer-trigger');
        card.setAttribute('tabindex', '0');
      });
    }

    getGalleryImageCards() {
      return Array.from(this.gallery.querySelectorAll('[data-media-id][data-media-type="image"]')).filter(
        card => !card.closest('.main-product__media--thumbs-wrapper')
      );
    }

    indexFor(mediaId) {
      const slides = Array.from(this.swiperEl.querySelectorAll('[data-viewer-media-id]'));
      const index = slides.findIndex(slide => slide.dataset.viewerMediaId === String(mediaId));
      return index === -1 ? null : index;
    }

    getCardFromEvent(event) {
      const card = event.target.closest('[data-media-id]');
      if (!card || !this.gallery.contains(card)) return null;
      if (card.dataset.mediaType !== 'image') return null; // videos and 3D models keep their own behaviour
      if (card.closest('.main-product__media--thumbs-wrapper')) return null; // thumbnails just change the slide
      if (event.target.closest('a, button, model-viewer, video, iframe')) return null;
      return card;
    }

    onGalleryClick(event) {
      const card = this.getCardFromEvent(event);
      if (!card) return;

      // Ignore the click that ends a swipe on Release's mobile slider
      const slider = card.closest('swiper-product-gallery')?.instance;
      if (slider && slider.allowClick === false) return;

      const index = this.indexFor(card.dataset.mediaId);
      if (index === null) return;
      this.open(index, card);
    }

    onGalleryKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const card = this.getCardFromEvent(event);
      if (!card || event.target !== card) return;
      event.preventDefault();
      const index = this.indexFor(card.dataset.mediaId);
      if (index !== null) this.open(index, card);
    }

    open(index, trigger) {
      if (typeof Swiper === 'undefined') return;
      this.lastTrigger = trigger || null;

      this.viewer.showModal();
      document.body.classList.add('overflow-hidden');

      this.initSwiper();
      this.swiper.update();
      this.updateZoomLimits();
      this.swiper.slideTo(index, 0);
      this.updateCounter();
      this.closeButton?.focus({ preventScroll: true });
    }

    onClose() {
      document.body.classList.remove('overflow-hidden');
      if (this.swiper?.zoom?.scale > 1) this.swiper.zoom.out();
      this.lastTrigger?.focus({ preventScroll: true });
    }

    initSwiper() {
      if (this.swiper) return;

      const updateCounter = () => this.updateCounter();
      const setLimits = () => this.updateZoomLimits();

      // Square slides: stacked vertically on mobile, side by side at full screen height on desktop
      this.swiper = new Swiper(this.swiperEl, {
        direction: 'vertical',
        slidesPerView: 'auto',
        spaceBetween: 0,
        freeMode: { enabled: true, sticky: false },
        mousewheel: true,
        keyboard: { enabled: true },
        // Pinch or double-tap to zoom the image only
        zoom: { maxRatio: 1, minRatio: 1, toggle: true },
        breakpoints: {
          750: { direction: 'horizontal' }
        },
        on: {
          slideChange: updateCounter,
          reachEnd: updateCounter,
          transitionEnd: updateCounter,
          touchEnd: updateCounter,
          // Free mode doesn't update the active slide while gliding, so work it out as it moves
          setTranslate: swiper => {
            swiper.updateActiveIndex();
            updateCounter();
          },
          resize: setLimits,
          touchStart: setLimits,
          zoomChange: (swiper, scale, imageEl) => {
            if (scale > 1) this.loadFullSize(imageEl);
          }
        }
      });
    }

    updateCounter() {
      if (!this.counter || !this.swiper) return;
      const total = this.swiper.slides.length;
      const current = this.swiper.isEnd ? total : this.swiper.activeIndex + 1;
      this.counter.textContent = `${Math.min(current, total)} / ${total}`;
    }

    /** Cap zoom so an image never goes past 100% of its real pixel size */
    updateZoomLimits() {
      if (!this.swiper) return;
      this.swiperEl.querySelectorAll('.swiper-zoom-container').forEach(container => {
        const img = container.querySelector('img');
        const boxWidth = container.offsetWidth;
        const boxHeight = container.offsetHeight;
        const realWidth = parseFloat(container.dataset.zoomWidth);
        const aspect = parseFloat(container.dataset.zoomAspect);
        if (!img || !boxWidth || !boxHeight || !realWidth || !aspect) return;

        const realHeight = realWidth / aspect;
        const fit = getComputedStyle(img).objectFit;
        const drawnScale =
          fit === 'cover'
            ? Math.max(boxWidth / realWidth, boxHeight / realHeight)
            : Math.min(boxWidth / realWidth, boxHeight / realHeight);
        container.setAttribute('data-swiper-zoom', Math.max(1, 1 / drawnScale).toFixed(2));
      });
    }

    /** Swap in the full-size file once someone zooms, so the image stays sharp */
    loadFullSize(imageEl) {
      if (!imageEl || imageEl.dataset.fullSize) return;
      const src = imageEl.closest('.swiper-zoom-container')?.dataset.zoomSrc;
      if (!src) return;
      imageEl.dataset.fullSize = 'loading';
      const full = new Image();
      full.onload = () => {
        imageEl.removeAttribute('srcset');
        imageEl.removeAttribute('sizes');
        imageEl.src = src;
        imageEl.dataset.fullSize = 'loaded';
      };
      full.onerror = () => {
        delete imageEl.dataset.fullSize;
      };
      full.src = src;
    }
  }

  customElements.define('product-image-viewer', ProductImageViewer);
}
